
import React, { useEffect, useState } from 'react';
import { ActionResponse, ActionStatusHelper, DateFormatHelper } from '@/Shared';
import { usePlanning } from '@/OneLiners';
import { CalendarRow, Resource } from '@/Shared/UI/Widgets/CalendarWidget/CalendarWidget';


const colorsByResource: Record<number | string, { background: string; color: string }> = {
    "reunion": { background: '#fee2e2', color: '#991b1b' },
    2: { background: '#ffedd5', color: '#9a3412' },
    3: { background: '#fef9c3', color: '#92400e' },
    1: { background: '#dcfce7', color: '#166534' },
};

// Transformer l'objet en tableau
const GLOBAL_RESOURCES = Object.entries(colorsByResource).map(([key, colorData]) => ({
    key,
    title: `Agent #${key}`, // ou un vrai nom si tu en as
    backgroundColor: colorData.background,
    color: colorData.color,
}));


/**
 * usePlanningWidget hook – Manages planning list configuration, state, and user interactions.
 *
 * This hook centralizes logic to:
 * - Fetch and enrich planning entities with additional data
 * - Provide configuration for DataTable or DataCardList components
 * - Handle row actions: view, create, edit, delete plannings
 * - Manage toast notifications to inform users about success or errors
 * - Expose status flags for fetching and deleting to control UI feedback
 *
 * Returns:
 * - planningConfig: DataTableConfig object used by UI components
 * - isFetchRunning, isFetchFailed, fetchErrorMessage: status of planning list fetching
 * - isDeleteSucceeded, isDeleteFailed, deleteErrorMessage: status of delete operation
 * - setDeleteIdle: resets delete status to 'idle' (used to clear delete state)
 * 
 * Internally:
 * - Uses enums and lists from app state for building headers and enrichment
 * - Transforms raw planning models into view models with `PlanningListViewModel`
 * - Handles navigation via hooks to detail and form views
 *
 * Author: Samano CASTRE  
 * Date: August 3, 2025
 */
export function usePlanningWidget() {

    const {
        dataMap,
        stateResponse
    } = usePlanning();

    const [response, setResponse] = useState<ActionResponse>({ status: 'loading' });

    const [data, setData] = useState<{ rows: CalendarRow[], resources: Resource[] }>();

    useEffect(() => {
        if (ActionStatusHelper.isSucceeded(stateResponse.status)) {
            const rowList = dataMap.toArray().map(planning => {
                return {
                    id: planning.id,
                    calendarDate: DateFormatHelper.toInput(planning.planningDate),  // e.g. '2025-10-05'
                    startTime: planning.startTime,     // e.g. '09:00'
                    endTime: planning.endTime,       // e.g. '10:30'
                    note: planning.note,
                    type: planning.type,
                    propertyId: planning.propertyId,
                    person: planning.agent?.person
                } as CalendarRow
            });
            setData({
                rows: rowList,
                resources: GLOBAL_RESOURCES
            })

        }
        setResponse(stateResponse);
    }, [stateResponse.status]);


    return {
        planningMap : dataMap,
        data,
        response
    }
}